import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ReactNode, SetStateAction } from 'react';
import { Pagination } from '../../../../lib/interfaces/utils';
import { cn } from '../../../../lib/utils/cn';
import { PaginationControls } from '../../../../ui/components/ui/pagination-controls';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../ui/components/ui/table';

const SKELETON_ROWS = 3;

export interface DataTableProps<TData extends { _id: string }> {
  columns: ColumnDef<TData>[];
  data: TData[];
  pagination: Pagination;
  pageCount: number;
  onPaginationChange: (next: SetStateAction<Pagination>) => void;
  isLoading: boolean;
  emptyState: ReactNode;
  onRowClick?: (row: TData) => void;
}

/** Server-paginated table in the banner admin look (pill rows, `PaginationControls` below). */
const DataTable = <TData extends { _id: string }>({
  columns,
  data,
  pagination,
  pageCount,
  onPaginationChange,
  isLoading,
  emptyState,
  onRowClick
}: DataTableProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    pageCount,
    manualPagination: true,
    onPaginationChange,
    getRowId: (row) => row._id,
    getCoreRowModel: getCoreRowModel(),
    state: { pagination }
  });

  const rows = table.getRowModel().rows;
  // Keeps "zurück" reachable when the current page ran empty (e.g. its last item was archived).
  const showPagination = rows.length > 0 || pagination.pageIndex > 0;

  if (isLoading) return <DataTableSkeleton />;

  return (
    <div className="w-full">
      {rows.length === 0 ? (
        emptyState
      ) : (
        <Table className="border-separate border-spacing-y-4 table-fixed w-full">
          <colgroup>
            {table.getVisibleLeafColumns().map((column) => (
              <col key={column.id} style={{ width: column.columnDef.size ?? 'auto' }} />
            ))}
          </colgroup>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} className="text-[#D0D5DD] h-12 font-semibold">
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const cells = row.getVisibleCells();
              return (
                <TableRow
                  key={row.id}
                  className={cn('bg-white transition-colors duration-200 hover:bg-gray-50', onRowClick && 'cursor-pointer')}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}>
                  {cells.map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        'border-none p-4 align-middle',
                        index === 0 && 'rounded-l-[61px]',
                        index === cells.length - 1 && 'rounded-r-[61px]'
                      )}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {showPagination && <PaginationControls table={table} />}
    </div>
  );
};

export default DataTable;

export const DataTableSkeleton = () => (
  <div className="flex flex-col gap-4 pt-16">
    {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
      <div key={i} className="bg-white rounded-[61px] p-4 flex items-center gap-5 animate-pulse">
        <div className="h-4 w-48 bg-[#E4E7EC] rounded-full" />
        <div className="flex-1" />
        <div className="h-4 w-20 bg-[#E4E7EC] rounded-full" />
        <div className="w-20 h-6 bg-[#E4E7EC] rounded-full" />
        <div className="w-8 h-8 bg-[#E4E7EC] rounded-full" />
      </div>
    ))}
  </div>
);
